import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Search, Pencil, Loader2, X, Save, Crown, Trash2, KeyRound } from "lucide-react";

const PLANS = [
  { id: "", label: "— (Free)" },
  { id: "plan_trial", label: "Trial" },
  { id: "plan_active", label: "FAS Active" },
  { id: "plan_pro", label: "FAS Pro" },
  { id: "admin", label: "Admin" },
];
const LEGACY = { trial: "Trial", active: "FAS Active", pro: "FAS Pro", plan_trial: "Trial", plan_active: "FAS Active", plan_pro: "FAS Pro", admin: "Admin" };
const normalizePlan = (p) => ({ trial: "plan_trial", active: "plan_active", pro: "plan_pro" }[p] || p || "");
const planLabel = (p) => LEGACY[p] || (PLANS.find((x) => x.id === (p || ""))?.label) || p || "—";
const fmtDate = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "-"; } };
const toDateInput = (iso) => { if (!iso) return ""; try { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; } catch { return ""; } };

function EditModal({ c, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: c.name || "", phone: c.phone || "", admin_note: c.admin_note || "",
    membership_plan: normalizePlan(c.membership_plan), membership_expires: toDateInput(c.membership_expires),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/customers/${c.user_id}`, form);
      toast.success("Data pelanggan disimpan.");
      onSaved(r.data.customer);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="card w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-customer-modal">
        <div className="flex items-center justify-between mb-4">
          <p className="font-heading font-bold">Edit Pelanggan</p>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"><X size={16} /></button>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-4 font-mono-data">{c.email}</p>
        <div className="space-y-3">
          <Field label="Nama"><input data-testid="edit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50" /></Field>
          <Field label="No. WhatsApp / HP"><input data-testid="edit-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="mis. 0812xxxx" className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Paket">
              <select data-testid="edit-plan" value={form.membership_plan} onChange={(e) => set("membership_plan", e.target.value)} className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50">
                {PLANS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Kedaluwarsa">
              <input data-testid="edit-expires" type="date" value={form.membership_expires} onChange={(e) => set("membership_expires", e.target.value)} className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50" />
            </Field>
          </div>
          <Field label="Catatan Admin"><textarea data-testid="edit-note" value={form.admin_note} onChange={(e) => set("admin_note", e.target.value)} rows={3} className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50 h-auto py-2 resize-none" /></Field>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving} data-testid="save-customer-btn" className="flex-1 h-10 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Simpan
          </button>
          <button onClick={onClose} className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold">Batal</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-[var(--text-secondary)] mb-1 block">{label}</span>
    {children}
  </label>
);

function DeleteModal({ c, onClose, onDeleted }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) { toast.error("Masukkan PIN."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/customers/${c.user_id}/delete`, { pin: pin.trim() });
      toast.success("Pelanggan dihapus.");
      onDeleted(c.user_id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <form onSubmit={submit} className="card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="delete-customer-modal">
        <div className="flex items-center gap-2 mb-3 text-bear"><Trash2 size={18} /><p className="font-heading font-bold">Hapus Pelanggan</p></div>
        <p className="text-sm text-[var(--text-secondary)] mb-1">Anda akan menghapus <b className="text-[var(--text-primary)]">{c.name || c.email}</b> secara permanen.</p>
        <p className="text-xs text-[var(--text-muted)] mb-4 font-mono-data">{c.email}</p>
        <label className="block mb-4">
          <span className="text-xs text-[var(--text-secondary)] mb-1 flex items-center gap-1.5"><KeyRound size={12} /> Masukkan PIN untuk konfirmasi</span>
          <input data-testid="delete-pin-input" type="password" inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••••" className="w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm tracking-widest outline-none focus:border-[var(--bear)]/60" />
        </label>
        <div className="flex gap-2">
          <button type="submit" disabled={busy} data-testid="confirm-delete-btn" className="flex-1 h-10 rounded-lg bg-[var(--bear)] text-white font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Hapus Permanen
          </button>
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] font-semibold">Batal</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback((query = "") => {
    setLoading(true);
    api.get(`/admin/customers${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => { setCustomers(r.data.customers || []); setCounts({ total: r.data.total || 0, active: r.data.active || 0 }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (updated) => setCustomers((list) => list.map((c) => (c.user_id === updated.user_id ? updated : c)));

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Users size={16} className="text-accent" />
          <span><b className="text-[var(--text-primary)]">{counts.total}</b> pelanggan · <b className="text-bull">{counts.active}</b> aktif</span>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="sm:ml-auto flex items-center gap-2 bg-[var(--surface-card)] border border-[var(--border)] rounded-lg px-3 h-10 w-full sm:w-72">
          <Search size={15} className="text-[var(--text-muted)]" />
          <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari email / nama / HP" className="bg-transparent flex-1 text-sm outline-none" />
        </form>
      </div>

      {loading ? (
        <div className="card p-12 flex items-center justify-center"><Loader2 className="animate-spin text-accent" /></div>
      ) : customers.length === 0 ? (
        <div className="card p-12 text-center text-[var(--text-muted)]">Belum ada pelanggan.</div>
      ) : (
        <div className="space-y-2">
          {customers.map((c) => (
            <div key={c.user_id} className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`customer-${c.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{c.name || "(tanpa nama)"}</span>
                  {c.is_admin && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-accent inline-flex items-center gap-1"><Crown size={10} /> ADMIN</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${c.membership_active ? "bg-[var(--bull)]/15 text-bull" : "bg-[var(--surface-hover)] text-[var(--text-secondary)]"}`}>
                    {c.membership_active ? "AKTIF" : "NONAKTIF"}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-0.5 font-mono-data">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Paket: <b>{planLabel(c.membership_plan)}</b>
                  {" · "}Sisa: <b className={c.days_left != null && c.days_left <= 3 && c.membership_active ? "text-amber" : "text-[var(--text-primary)]"}>{c.is_admin ? "∞" : c.days_left != null ? `${c.days_left} hari` : "-"}</b>
                  {c.membership_expires && !c.is_admin ? ` (s/d ${fmtDate(c.membership_expires)})` : ""}
                </p>
                {c.admin_note && <p className="text-[11px] text-[var(--text-muted)] mt-1 italic">📝 {c.admin_note}</p>}
              </div>
              <button onClick={() => setEditing(c)} data-testid={`edit-customer-${c.user_id}`} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] text-sm font-semibold hover:border-[var(--accent)]/40 hover:text-accent transition-colors">
                <Pencil size={14} /> Edit
              </button>
              {!c.is_admin && (
                <button onClick={() => setDeleting(c)} data-testid={`delete-customer-${c.user_id}`} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] text-sm font-semibold text-[var(--text-secondary)] hover:border-[var(--bear)]/40 hover:text-bear transition-colors">
                  <Trash2 size={14} /> Hapus
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && <EditModal c={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
      {deleting && <DeleteModal c={deleting} onClose={() => setDeleting(null)} onDeleted={(id) => setCustomers((list) => list.filter((x) => x.user_id !== id))} />}
    </div>
  );
}
